import { GraphQLClient } from "graphql-request";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { UserContext } from "./UserProvider";
import AppConfig from "../config/envConfig";
import React from "react";

interface ApiProviderProps {
  children: ReactNode;
}

interface ApiProviderContextValue {
  graphQLClient: GraphQLClient | undefined;
  isReady: boolean;
}

export const ApiProviderContext = React.createContext<ApiProviderContextValue>({
  graphQLClient: undefined,
  isReady: false,
});

const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
  const timerRef = useRef<number | undefined>(undefined);
  const [graphQLClient, setGraphQLClient] = useState<GraphQLClient | undefined>(
    undefined
  );
  const { userSession, setUserSession } = useContext(UserContext);
  const [isReady, setIsReady] = useState(false);

  const createClient = useCallback(
    (token: string) => {
      if (graphQLClient) {
        graphQLClient.setHeader("Authorization", token);
      } else {
        const client = new GraphQLClient(AppConfig.apiUrl, {
          headers: {
            "Access-Control-Allow-Origin":
              "http://localhost:3000, https://dev.workaday.com, https://dev.nextlevelconcierge.ca",
            Authorization: `${token}`,
          },
        });
        setGraphQLClient(client);
      }
    },
    [graphQLClient]
  );

  const refreshToken = useCallback(() => {
    try {
      setIsReady(false);
      setUserSession();
      setIsReady(true);
    } catch (_e) {
      //cleanup our interval
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
    }
  }, [setUserSession]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (timerRef?.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
    timerRef.current = window.setInterval(
      refreshToken,
      AppConfig.refreshTokenTimer
    );
  }, [refreshToken]);

  useEffect(() => {
    if (userSession?.jwt) {
      createClient(userSession.jwt);
      setIsReady(true);
    }
  }, [userSession, refreshToken, createClient]);

  return (
    <ApiProviderContext.Provider value={{ graphQLClient, isReady }}>
      {children}
    </ApiProviderContext.Provider>
  );
};

export default ApiProvider;
